import { useCallback, useEffect } from 'react';
import { signalsAPI } from '@/api/endpoints';
import { useSignalsStore } from '@/store/signalsStore';
import { useTelegram } from './useTelegram';
import type { CreateSignalRequest, UpdateSignalRequest, Signal } from '@/api/types';

interface UseSignalsReturn {
  signals: Signal[];
  currentPrices: Record<string, number>;
  isLoading: boolean;
  error: string | null;
  fetchSignals: () => Promise<void>;
  createSignal: (data: CreateSignalRequest) => Promise<Signal[] | null>;
  updateSignal: (id: string, data: UpdateSignalRequest) => Promise<Signal | null>;
  deleteSignal: (id: string) => Promise<boolean>;
  toggleSignalActive: (id: string, active: boolean, direction: string) => Promise<Signal | null>;
  clearError: () => void;
}

export const useSignals = (): UseSignalsReturn => {
  const {
    signals,
    currentPrices,
    isLoading,
    error,
    setSignals,
    addSignal,
    updateSignal: updateSignalInStore,
    removeSignal,
    setLoading,
    setError,
  } = useSignalsStore();

  const { notificationOccurred, impactOccurred } = useTelegram();

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  const fetchSignals = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const data = await signalsAPI.getAll();
      setSignals(data);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to fetch signals';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [setSignals, setLoading, setError]);

  const createSignal = useCallback(
    async (data: CreateSignalRequest): Promise<Signal[] | null> => {
      setLoading(true);
      setError(null);

      try {
        const newSignals = await signalsAPI.create(data);

        // Add all created signals to store
        newSignals.forEach((signal) => addSignal(signal));

        impactOccurred('heavy');
        notificationOccurred('success');

        return newSignals;
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to create signal';
        setError(errorMessage);
        notificationOccurred('error');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [addSignal, setLoading, setError, impactOccurred, notificationOccurred]
  );

  const updateSignal = useCallback(
    async (id: string, data: UpdateSignalRequest): Promise<Signal | null> => {
      setLoading(true);
      setError(null);

      try {
        const updated = await signalsAPI.update(id, data);
        updateSignalInStore(id, updated);

        impactOccurred('medium');
        notificationOccurred('success');

        return updated;
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to update signal';
        setError(errorMessage);
        notificationOccurred('error');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [updateSignalInStore, setLoading, setError, impactOccurred, notificationOccurred]
  );

  const deleteSignal = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await signalsAPI.delete(id);
        removeSignal(id);

        impactOccurred('medium');
        notificationOccurred('success');

        return true;
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to delete signal';
        setError(errorMessage);
        notificationOccurred('error');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [removeSignal, setLoading, setError, impactOccurred, notificationOccurred]
  );

  const toggleSignalActive = useCallback(
    async (id: string, active: boolean, direction: string): Promise<Signal | null> => {
      try {
        const updated = await signalsAPI.toggleActive(id, active, direction);
        updateSignalInStore(id, { active });

        impactOccurred('light');

        return updated;
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to toggle signal';
        setError(errorMessage);
        notificationOccurred('error');
        return null;
      }
    },
    [updateSignalInStore, setError, impactOccurred, notificationOccurred]
  );

  // Fetch signals on mount
  useEffect(() => {
    fetchSignals();
  }, [fetchSignals]);

  return {
    signals,
    currentPrices,
    isLoading,
    error,
    fetchSignals,
    createSignal,
    updateSignal,
    deleteSignal,
    toggleSignalActive,
    clearError,
  };
};
